import type { MatrixData } from '../components/MatrixTable'

import { interpolateSFR, type WaterRow } from './feedCalculations'
import { computeSgrPercentFromWeights } from './lotGrowthFormulas'
import { canonicalizeLotToken, resolveCanonicalLotKey, resolveLegacyLotIdentity } from './lotIdentity'
import { getZonedWeekKey, resolveTimeZone } from './timeBucketing'
import { buildLatestDatedRowByKey, buildLatestDatedRowByKeyInWindow } from './waterSelection'

export { resolveCanonicalLotKey } from './lotIdentity'

const MS_PER_DAY = 86_400_000

export type DataRowSource = 'api' | 'local'

export type DatabaseRowForDashboardKpis = {
  id?: string
  lot?: string
  lotId?: string
  date?: string
  weight?: string | number | null
  fishStock?: string | number | null
  _farmKey?: string
  _canonicalFarmKey?: string
  _canonicalProductionUnitKey?: string
  _canonicalLotKey?: string
  _source?: DataRowSource
  _displayFarmLabel?: string
}

export type CanonicalLotGroupForDashboardKpis = {
  canonicalProductionUnitKey: string
  canonicalLotKey: string
  displayLabel: string
  rows: DatabaseRowForDashboardKpis[]
}

export type HistoricalRowMatch<Row extends DatabaseRowForDashboardKpis = DatabaseRowForDashboardKpis> = Row & {
  _matchedFor: string
}

export type FishStockDebugInfo<Row extends DatabaseRowForDashboardKpis = DatabaseRowForDashboardKpis> = {
  current: number
  lastMonth: number
  trend: number
  currentWeekLabel?: string
  lastMonthWeekLabel?: string
  currentRows: Row[]
  lastMonthRows: Array<HistoricalRowMatch<Row>>
}

const resolveWaterRowLotIdentity = (row: WaterRow) => (
  resolveLegacyLotIdentity(
    { source: 'lotId', value: (row as { lotId?: string }).lotId },
    { source: 'lot', value: row.lot }
  )
)

const resolveDashboardKpiRowLotIdentity = (row: DatabaseRowForDashboardKpis) => (
  resolveLegacyLotIdentity(
    { source: 'lotId', value: row.lotId },
    { source: 'canonicalLotKey', value: row._canonicalLotKey },
    { source: 'lot', value: row.lot },
    { source: 'rowId', value: row.id }
  )
)

const toDashboardLotToken = (value: string | number | null | undefined): string => {
  const resolved = resolveCanonicalLotKey(value)
  if (!resolved || resolved === 'unknown-lot') return ''
  const token = canonicalizeLotToken(resolved)
  return token && token !== 'unknownlot' ? token : ''
}

const resolveDashboardKpiRowLotTokens = (row: DatabaseRowForDashboardKpis): string[] => {
  const tokens: string[] = []
  const addToken = (value: string | null | undefined) => {
    const token = toDashboardLotToken(value)
    if (!token) return
    if (!tokens.includes(token)) tokens.push(token)
  }

  addToken(row.lotId)
  addToken(row._canonicalLotKey)
  addToken(row.lot)
  addToken(row.id)

  return tokens
}

const resolveCanonicalLotGroupToken = (group: CanonicalLotGroupForDashboardKpis): string => {
  for (const row of group.rows) {
    const token = toDashboardLotToken(resolveDashboardKpiRowLotIdentity(row).token)
    if (token) {
      return token
    }
  }

  return toDashboardLotToken(group.canonicalLotKey)
}

const resolveCanonicalLotGroupTokens = (group: CanonicalLotGroupForDashboardKpis): string[] => {
  const tokens: string[] = []
  const addToken = (value: string | null | undefined) => {
    const token = toDashboardLotToken(value)
    if (!token) return
    if (!tokens.includes(token)) tokens.push(token)
  }

  group.rows.forEach((row) => {
    resolveDashboardKpiRowLotTokens(row).forEach((token) => addToken(token))
  })

  addToken(group.canonicalLotKey)

  return tokens
}

const buildCanonicalLotTokenAliasMap = (groups: CanonicalLotGroupForDashboardKpis[]) => {
  const aliasToPrimary = new Map<string, string>()
  const primaryTokens = new Set<string>()

  groups.forEach((group) => {
    const primaryToken = resolveCanonicalLotGroupToken(group)
    if (!primaryToken) return

    primaryTokens.add(primaryToken)

    const aliases = resolveCanonicalLotGroupTokens(group)
    aliases.forEach((aliasToken) => {
      aliasToPrimary.set(aliasToken, primaryToken)
    })
    aliasToPrimary.set(primaryToken, primaryToken)
  })

  return { aliasToPrimary, primaryTokens }
}

export const parseFishStock = (input: string | number | null | undefined): number => {
  if (typeof input === 'number') {
    return Number.isFinite(input) ? input : 0
  }
  if (typeof input === 'string') {
    const normalized = input.replace(/[^\d-]/g, '')
    const parsed = Number.parseInt(normalized, 10)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

export const parseWeight = (input: string | number | null | undefined): number => {
  if (typeof input === 'number') {
    return Number.isFinite(input) ? input : 0
  }

  if (typeof input === 'string') {
    const trimmed = input.trim()
    if (trimmed.length === 0) {
      return 0
    }

    let sanitized = trimmed
      .replace(/[^0-9.,-\s]/g, '')
      .replace(/\s+/g, '')

    if (sanitized.length === 0) {
      return 0
    }

    const lastComma = sanitized.lastIndexOf(',')
    const lastDot = sanitized.lastIndexOf('.')

    if (lastComma !== -1 || lastDot !== -1) {
      let decimalSeparator: ',' | '.' | null = null

      if (lastComma > lastDot) {
        decimalSeparator = ','
      } else if (lastDot > lastComma) {
        decimalSeparator = '.'
      } else {
        decimalSeparator = lastComma !== -1 ? ',' : lastDot !== -1 ? '.' : null
      }

      if (decimalSeparator === ',') {
        sanitized = sanitized.replace(/\./g, '')
        sanitized = sanitized.replace(/,/g, '.')
      } else if (decimalSeparator === '.') {
        sanitized = sanitized.replace(/,/g, '')
      }
    } else {
      sanitized = sanitized.replace(/[.,]/g, '')
    }

    sanitized = sanitized
      .replace(/(?!^)-/g, '')
      .replace(/\.(?=.*\.)/g, '')

    const parsed = Number.parseFloat(sanitized)
    return Number.isFinite(parsed) ? parsed : 0
  }

  if (input == null) return 0

  const parsed = Number.parseFloat(String(input))
  return Number.isFinite(parsed) ? parsed : 0
}

export const getWeekKey = (value: Date | string | null | undefined): string | null => {
  if (!value) return null
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return null

  const yearStart = new Date(date.getFullYear(), 0, 1)
  const diffDays = ((date.getTime() - yearStart.getTime()) / MS_PER_DAY) + yearStart.getDay() + 1
  const weekNum = Math.ceil(diffDays / 7)

  return `${date.getFullYear()}-W${weekNum.toString().padStart(2, '0')}`
}

export const getWeekKeyInTimeZone = (
  value: Date | string | null | undefined,
  timeZone?: string
): string | null => {
  if (!value) return null
  const resolved = resolveTimeZone(timeZone)
  return getZonedWeekKey(value, resolved)
}

// T3-D-04c (2026-07-10): row-level stage eligibility gate retired.
// Home KPI cards previously filtered on `row.stage === 'Active'` (with a
// legacy `_source === 'local'` inactive-with-data exception) but the
// Active/Inactive distinction on a production row is no longer
// operationally meaningful: `Lot.stage` on the identity record is the
// single source of truth for lifecycle (see docs/architecture-concerns.md
// invariant I-1). Consumers now accept every row that carries usable
// data; the individual KPI functions still apply the appropriate
// `weight > 0` / `stock > 0` / week-range / lot-scope filters below.

export type LotTimelineEntry<Row extends DatabaseRowForDashboardKpis = DatabaseRowForDashboardKpis> = {
  row: Row
  index: number
  date: Date
  weight: number
  stock: number
  rawWeight: string
  rawStock: string
  source: DataRowSource
}

export type LotEntryRequirements = {
  requireWeight?: boolean
  requireStock?: boolean
}

export const findLatestEntryForWeek = <Row extends DatabaseRowForDashboardKpis>(
  rows: Row[],
  weekKey: string | null,
  requirements: LotEntryRequirements = { requireStock: true },
  timeZone?: string
): LotTimelineEntry<Row> | null => {
  if (!weekKey) return null
  let best: LotTimelineEntry<Row> | null = null
  const requireWeight = requirements.requireWeight ?? false
  const requireStock = requirements.requireStock ?? true

  rows.forEach((row, index) => {
    if (!row.date) return
    const rowWeekKey = getWeekKeyInTimeZone(row.date, timeZone)
    if (rowWeekKey !== weekKey) return
    const date = new Date(row.date)
    if (Number.isNaN(date.getTime())) return

    const weight = parseWeight(row.weight)
    const stock = parseFishStock(row.fishStock)
    const rawWeight = typeof row.weight === 'string' ? row.weight : row.weight != null ? String(row.weight) : ''
    const rawStock = typeof row.fishStock === 'string' ? row.fishStock : row.fishStock != null ? String(row.fishStock) : ''

    if (requireWeight && weight <= 0) return
    if (requireStock && stock <= 0) return

    if (!best || date.getTime() > best.date.getTime()) {
      best = {
        row,
        index,
        date,
        weight,
        stock,
        rawWeight,
        rawStock,
        source: (row._source ?? 'local') as DataRowSource
      }
    }
  })

  return best
}

export const findClosestEntryForWeek = <Row extends DatabaseRowForDashboardKpis>(
  rows: Row[],
  weekKey: string | null,
  referenceDate: Date,
  requirements: LotEntryRequirements = { requireStock: true },
  timeZone?: string
): LotTimelineEntry<Row> | null => {
  if (!weekKey) return null
  let best: LotTimelineEntry<Row> | null = null
  const requireWeight = requirements.requireWeight ?? false
  const requireStock = requirements.requireStock ?? true

  rows.forEach((row, index) => {
    if (!row.date) return
    const rowWeekKey = getWeekKeyInTimeZone(row.date, timeZone)
    if (rowWeekKey !== weekKey) return
    const date = new Date(row.date)
    if (Number.isNaN(date.getTime())) return

    const weight = parseWeight(row.weight)
    const stock = parseFishStock(row.fishStock)
    const rawWeight = typeof row.weight === 'string' ? row.weight : row.weight != null ? String(row.weight) : ''
    const rawStock = typeof row.fishStock === 'string' ? row.fishStock : row.fishStock != null ? String(row.fishStock) : ''

    if (requireWeight && weight <= 0) return
    if (requireStock && stock <= 0) return

    if (!best) {
      best = {
        row,
        index,
        date,
        weight,
        stock,
        rawWeight,
        rawStock,
        source: (row._source ?? 'local') as DataRowSource
      }
      return
    }

    const candidateDiff = Math.abs(date.getTime() - referenceDate.getTime())
    const bestDiff = Math.abs(best.date.getTime() - referenceDate.getTime())

    if (candidateDiff < bestDiff || (candidateDiff === bestDiff && date.getTime() > best.date.getTime())) {
      best = {
        row,
        index,
        date,
        weight,
        stock,
        rawWeight,
        rawStock,
        source: (row._source ?? 'local') as DataRowSource
      }
    }
  })

  return best
}

export const findPreviousValidEntry = <Row extends DatabaseRowForDashboardKpis>(
  rows: Row[],
  startIndex: number,
  options?: {
    excludeWeekKey?: string | null
    requirements?: LotEntryRequirements
    timeZone?: string
  }
): LotTimelineEntry<Row> | null => {
  const requireWeight = options?.requirements?.requireWeight ?? false
  const requireStock = options?.requirements?.requireStock ?? true

  for (let idx = startIndex - 1; idx >= 0; idx--) {
    const candidate = rows[idx]
    if (!candidate?.date) continue
    const candidateDate = new Date(candidate.date)
    if (Number.isNaN(candidateDate.getTime())) continue

    const candidateWeekKey = getWeekKeyInTimeZone(candidate.date, options?.timeZone)
    if (options?.excludeWeekKey && candidateWeekKey === options.excludeWeekKey) {
      continue
    }

    const weight = parseWeight(candidate.weight)
    const stock = parseFishStock(candidate.fishStock)
    const rawWeight = typeof candidate.weight === 'string'
      ? candidate.weight
      : candidate.weight != null
        ? String(candidate.weight)
        : ''
    const rawStock = typeof candidate.fishStock === 'string'
      ? candidate.fishStock
      : candidate.fishStock != null
        ? String(candidate.fishStock)
        : ''

    if (requireWeight && weight <= 0) continue
    if (requireStock && stock <= 0) continue

    return {
      row: candidate,
      index: idx,
      date: candidateDate,
      weight,
      stock,
      rawWeight,
      rawStock,
      source: (candidate._source ?? 'local') as DataRowSource
    }
  }

  return null
}

export const computeFishStockKpi = <Row extends DatabaseRowForDashboardKpis>(args: {
  rowsByDisplayKey: Record<string, Row[]>
  now?: Date
}): {
  displayTotal: number
  trendPercent: number
  activeRows: Row[]
  debug: FishStockDebugInfo<Row> | null
} => {
  const now = args.now ?? new Date()
  const currentWeekKey = getWeekKey(now)
  if (!currentWeekKey) {
    return {
      displayTotal: 0,
      trendPercent: 0,
      activeRows: [],
      debug: null
    }
  }

  const lastMonthDate = new Date(now)
  lastMonthDate.setDate(now.getDate() - 30)
  const lastMonthWeekKey = getWeekKey(lastMonthDate)

  const seenIds = new Set<string>()
  let currentSum = 0
  const currentRows: Row[] = []

  Object.values(args.rowsByDisplayKey).forEach((rows) => {
    rows.forEach((row) => {
      if (!row.id || seenIds.has(row.id)) return
      if (!row.date) return
      if (getWeekKey(row.date) !== currentWeekKey) return

      const value = parseFishStock(row.fishStock)
      if (value <= 0) return

      currentSum += value
      seenIds.add(row.id)
      currentRows.push(row)
    })
  })

  const lastMonthRowsByKey: Record<string, Row[]> = {}
  const windowDays = 60

  Object.values(args.rowsByDisplayKey).forEach((rows) => {
    rows.forEach((row) => {
      if (!row.date) return
      const rowDate = new Date(row.date)
      if (Number.isNaN(rowDate.getTime())) return

      const diffDays = Math.abs(rowDate.getTime() - lastMonthDate.getTime()) / MS_PER_DAY
      if (diffDays > windowDays) return

      const key = `${row._farmKey ?? ''}||${resolveDashboardKpiRowLotIdentity(row).token}`
      if (!lastMonthRowsByKey[key]) {
        lastMonthRowsByKey[key] = []
      }
      lastMonthRowsByKey[key].push(row)
    })
  })

  const matchedLastMonthRows: Array<HistoricalRowMatch<Row>> = []
  let lastMonthSum = 0

  currentRows.forEach((currentRow) => {
    const key = `${currentRow._farmKey ?? ''}||${resolveDashboardKpiRowLotIdentity(currentRow).token}`
    const candidates = lastMonthRowsByKey[key]
    if (!candidates || candidates.length === 0) return

    const sameWeekCandidates = lastMonthWeekKey
      ? candidates.filter((candidate) => getWeekKey(candidate.date) === lastMonthWeekKey)
      : []

    const candidatePool = sameWeekCandidates.length > 0 ? sameWeekCandidates : candidates

    const match = candidatePool.reduce((closest, candidate) => {
      const candidateDate = new Date(candidate.date ?? '')
      const closestDate = new Date(closest.date ?? '')
      const candidateDiff = Math.abs(candidateDate.getTime() - lastMonthDate.getTime())
      const closestDiff = Math.abs(closestDate.getTime() - lastMonthDate.getTime())

      if (candidateDiff === closestDiff) {
        return candidateDate.getTime() > closestDate.getTime() ? candidate : closest
      }

      return candidateDiff < closestDiff ? candidate : closest
    }, candidatePool[0])

    matchedLastMonthRows.push({
      ...(match as Row),
      _matchedFor: String(currentRow.id)
    })

    const matchValue = parseFishStock(match.fishStock)
    if (matchValue > 0) {
      lastMonthSum += matchValue
    }
  })

  const trend = lastMonthSum > 0
    ? ((currentSum - lastMonthSum) / Math.max(lastMonthSum, 1)) * 100
    : 0

  return {
    displayTotal: currentSum,
    trendPercent: trend,
    activeRows: currentRows,
    debug: {
      current: currentSum,
      lastMonth: lastMonthSum,
      trend,
      currentWeekLabel: currentWeekKey ?? undefined,
      lastMonthWeekLabel: lastMonthWeekKey ?? undefined,
      currentRows,
      lastMonthRows: matchedLastMonthRows
    }
  }
}

export const collectDashboardWaterBaseKeys = (canonicalLotGroups: CanonicalLotGroupForDashboardKpis[]): Set<string> => {
  const baseKeys = new Set<string>()
  const addCandidate = (value: string | null | undefined) => {
    if (!value) return
    const trimmed = value.trim()
    if (!trimmed) return
    baseKeys.add(trimmed)
  }

  canonicalLotGroups.forEach((group) => {
    group.rows.forEach((row) => {
      if (row._canonicalFarmKey && row._canonicalProductionUnitKey) {
        addCandidate(`${row._canonicalFarmKey}-${row._canonicalProductionUnitKey}`)
      }
      addCandidate(row._farmKey)
      addCandidate(row._farmKey?.replace(/\s*·\s*/g, '-'))
      addCandidate(row._displayFarmLabel)
      addCandidate(row._displayFarmLabel?.replace(/\s*·\s*/g, '-'))
    })

    if (baseKeys.size === 0 && group.displayLabel) {
      addCandidate(group.displayLabel)
      addCandidate(group.displayLabel.replace(/\s*·\s*/g, '-'))
    }
  })

  return baseKeys
}

export type AvgWaterTempKpiResult = {
  current: number
  previous: number | null
  trend: number
  matchedLots: number
  totalLots: number
}

export type WaterMetricKey = 'ph' | 'dissolvedOxygen' | 'salinity'

export type AvgWaterMetricKpiResult = {
  current: number
  previous: number | null
  trend: number
  matchedLots: number
  totalLots: number
}

export const computeAvgWaterTempKpi = (args: {
  canonicalLotGroups: CanonicalLotGroupForDashboardKpis[]
  waterRows: WaterRow[]
  now?: Date
}): AvgWaterTempKpiResult => {
  const now = args.now ?? new Date()
  const currentStart = new Date(now.getTime() - 7 * MS_PER_DAY)
  const previousStart = new Date(now.getTime() - 14 * MS_PER_DAY)
  const previousEnd = currentStart

  const { aliasToPrimary, primaryTokens } = buildCanonicalLotTokenAliasMap(args.canonicalLotGroups)

  const pickLatestPerLotInWindow = (start: Date, end: Date) => buildLatestDatedRowByKeyInWindow(
    args.waterRows,
    (row) => {
      if (row.temperature === null || row.temperature === undefined) return null

      const lotKey = toDashboardLotToken(resolveWaterRowLotIdentity(row).token)
      const primaryToken = aliasToPrimary.get(lotKey)
      return primaryToken ?? null
    },
    start,
    end
  )

  const currentLatest = pickLatestPerLotInWindow(currentStart, now)
  const previousLatest = pickLatestPerLotInWindow(previousStart, previousEnd)

  const currentTemps = Array.from(currentLatest.values())
    .map((row) => typeof row.temperature === 'number' ? row.temperature : Number.parseFloat(String(row.temperature)))
    .filter((temp) => Number.isFinite(temp))
  const previousTemps = Array.from(previousLatest.values())
    .map((row) => typeof row.temperature === 'number' ? row.temperature : Number.parseFloat(String(row.temperature)))
    .filter((temp) => Number.isFinite(temp))

  const currentAvg = currentTemps.length > 0
    ? currentTemps.reduce((sum, value) => sum + value, 0) / currentTemps.length
    : 0

  const previousAvg = previousTemps.length > 0
    ? previousTemps.reduce((sum, value) => sum + value, 0) / previousTemps.length
    : null

  const trend = previousAvg !== null && Number.isFinite(previousAvg)
    ? currentAvg - previousAvg
    : 0

  return {
    current: currentAvg,
    previous: previousAvg,
    trend,
    matchedLots: currentTemps.length,
    totalLots: primaryTokens.size
  }
}

export const computeAvgWaterMetricKpi = (args: {
  canonicalLotGroups: CanonicalLotGroupForDashboardKpis[]
  waterRows: WaterRow[]
  metric: WaterMetricKey
  now?: Date
}): AvgWaterMetricKpiResult => {
  const now = args.now ?? new Date()
  const currentStart = new Date(now.getTime() - 7 * MS_PER_DAY)
  const previousStart = new Date(now.getTime() - 14 * MS_PER_DAY)
  const previousEnd = currentStart

  const { aliasToPrimary, primaryTokens } = buildCanonicalLotTokenAliasMap(args.canonicalLotGroups)

  const pickLatestPerLotInWindow = (start: Date, end: Date) => buildLatestDatedRowByKeyInWindow(
    args.waterRows,
    (row) => {
      const rawValue = (row as any)?.[args.metric]
      if (rawValue === null || rawValue === undefined) return null

      const lotKey = toDashboardLotToken(resolveWaterRowLotIdentity(row).token)
      const primaryToken = aliasToPrimary.get(lotKey)
      return primaryToken ?? null
    },
    start,
    end
  )

  const currentLatest = pickLatestPerLotInWindow(currentStart, now)
  const previousLatest = pickLatestPerLotInWindow(previousStart, previousEnd)

  const currentValues = Array.from(currentLatest.values())
    .map((row) => {
      const rawValue = (row as any)?.[args.metric]
      return typeof rawValue === 'number' ? rawValue : Number.parseFloat(String(rawValue))
    })
    .filter((value) => Number.isFinite(value))
  const previousValues = Array.from(previousLatest.values())
    .map((row) => {
      const rawValue = (row as any)?.[args.metric]
      return typeof rawValue === 'number' ? rawValue : Number.parseFloat(String(rawValue))
    })
    .filter((value) => Number.isFinite(value))

  const currentAvg = currentValues.length > 0
    ? currentValues.reduce((sum, value) => sum + value, 0) / currentValues.length
    : 0

  const previousAvg = previousValues.length > 0
    ? previousValues.reduce((sum, value) => sum + value, 0) / previousValues.length
    : null

  const trend = previousAvg !== null && Number.isFinite(previousAvg)
    ? currentAvg - previousAvg
    : 0

  return {
    current: currentAvg,
    previous: previousAvg,
    trend,
    matchedLots: currentValues.length,
    totalLots: primaryTokens.size
  }
}

export type FeedDebugRow<Row extends DatabaseRowForDashboardKpis = DatabaseRowForDashboardKpis> = {
  farmKey: string
  lot: string
  week: string | null
  weight: number
  stock: number
  temp: number
  sfrPercent: number
  dailyFeedKg: number
  previousWeek?: string
  matchedPreviousWeek: boolean
  previousDailyFeedKg?: number
  deltaKg?: number
  deltaPercent?: number
  debug?: {
    formula: string
    expected: number
  }
}

export type FeedDebugSummary = {
  currentWeekKey?: string
  previousWeekKey?: string
  currentTotal: number
  previousTotal: number
  matchedLots: number
  totalLots: number
}

const buildWaterWeekMap = (waterRows: WaterRow[], timeZone?: string): Map<string, Map<string, WaterRow>> => {
  const byWeek = new Map<string, Map<string, WaterRow>>()
  const latestByWeekAndLot = buildLatestDatedRowByKey(waterRows, (waterRow) => {
    if (!waterRow?.date) return null
    if (waterRow.temperature === null || waterRow.temperature === undefined) return null

    const weekKey = getWeekKeyInTimeZone(waterRow.date, timeZone)
    if (!weekKey) return null

    const waterLotIdentity = resolveWaterRowLotIdentity(waterRow)
    if (waterLotIdentity.source === 'unknown') return null

    const canonicalLotKey = canonicalizeLotToken(waterLotIdentity.token)
    if (!canonicalLotKey) return null

    return `${weekKey}::${canonicalLotKey}`
  })

  for (const [selectionKey, waterRow] of latestByWeekAndLot.entries()) {
    const separatorIndex = selectionKey.indexOf('::')
    if (separatorIndex === -1) continue

    const weekKey = selectionKey.slice(0, separatorIndex)
    const canonicalLotKey = selectionKey.slice(separatorIndex + 2)
    if (!weekKey || !canonicalLotKey) continue

    if (!byWeek.has(weekKey)) {
      byWeek.set(weekKey, new Map())
    }

    byWeek.get(weekKey)!.set(canonicalLotKey, waterRow)
  }

  return byWeek
}

const resolveWaterRowForLot = (args: {
  weekMap: Map<string, WaterRow>
  canonicalLotKeys: string[]
}): WaterRow | undefined => {
  for (const key of args.canonicalLotKeys) {
    if (!key) continue
    const row = args.weekMap.get(key)
    if (row) return row
  }
  return undefined
}

export const computeActiveDailyFeedKpi = <Row extends DatabaseRowForDashboardKpis>(args: {
  canonicalLotGroups: Array<CanonicalLotGroupForDashboardKpis & { rows: Row[] }>
  waterRows: WaterRow[]
  now?: Date
  timeZone?: string
  species?: string
  sfrMatrix?: MatrixData | null
  resolveSfrMatrix?: (context: {
    group: CanonicalLotGroupForDashboardKpis & { rows: Row[] }
    currentRow: Row
  }) => MatrixData | null | undefined
  resolveSpecies?: (context: {
    group: CanonicalLotGroupForDashboardKpis & { rows: Row[] }
    currentRow: Row
  }) => string | undefined
}): {
  dailyFeedKg: number
  trendPercent: number
  debugRows: Array<FeedDebugRow<Row>>
  debugSummary: FeedDebugSummary | null
  missingPreviousLots: string[]
} => {
  const now = args.now ?? new Date()
  const currentWeekKey = getWeekKeyInTimeZone(now, args.timeZone)
  if (!currentWeekKey) {
    return {
      dailyFeedKg: 0,
      trendPercent: 0,
      debugRows: [],
      debugSummary: null,
      missingPreviousLots: []
    }
  }

  const previousWeekDate = new Date(now)
  previousWeekDate.setDate(now.getDate() - 7)
  const previousWeekKey = getWeekKeyInTimeZone(previousWeekDate, args.timeZone)

  const waterByWeek = buildWaterWeekMap(args.waterRows, args.timeZone)

  const debugRowByCanonical = new Map<string, FeedDebugRow<Row>>()
  const debugRows: Array<FeedDebugRow<Row>> = []
  const missingPreviousLots: string[] = []

  let currentFeedTotal = 0
  let previousFeedTotal = 0
  let lotCount = 0
  let matchedLotCount = 0

  args.canonicalLotGroups.forEach((group) => {
    const rows = group.rows
      .slice()
      .sort((a, b) => {
        const aTime = a.date ? new Date(a.date).getTime() : 0
        const bTime = b.date ? new Date(b.date).getTime() : 0
        return aTime - bTime
      })

    if (rows.length === 0) return

    const canonicalKey = `${group.canonicalProductionUnitKey}||${group.canonicalLotKey}`

    const currentCandidate = findLatestEntryForWeek(rows, currentWeekKey, {
      requireWeight: true,
      requireStock: true
    }, args.timeZone)

    if (!currentCandidate) {
      return
    }

    const currentWeekMap = waterByWeek.get(currentWeekKey)
    if (!currentWeekMap) {
      return
    }

    const canonicalLotKeys = [
      ...resolveDashboardKpiRowLotTokens(currentCandidate.row),
      ...resolveCanonicalLotGroupTokens(group)
    ]

    const waterEntry = resolveWaterRowForLot({
      weekMap: currentWeekMap,
      canonicalLotKeys
    })

    if (!waterEntry || waterEntry.temperature === null || waterEntry.temperature === undefined) {
      return
    }

    const temperature = typeof waterEntry.temperature === 'number'
      ? waterEntry.temperature
      : Number.parseFloat(String(waterEntry.temperature))

    if (!Number.isFinite(temperature)) {
      return
    }

    const effectiveMatrix = args.resolveSfrMatrix?.({
      group: group as CanonicalLotGroupForDashboardKpis & { rows: Row[] },
      currentRow: currentCandidate.row as Row
    }) ?? args.sfrMatrix
    const effectiveSpecies = args.resolveSpecies?.({
      group: group as CanonicalLotGroupForDashboardKpis & { rows: Row[] },
      currentRow: currentCandidate.row as Row
    }) ?? args.species

    const sfrResult = interpolateSFR(currentCandidate.weight, temperature, {
      species: effectiveSpecies,
      matrix: effectiveMatrix
    })
    const biomassKg = (currentCandidate.stock * currentCandidate.weight) / 1000
    const dailyFeedKg = (biomassKg * sfrResult.sfr) / 100
    if (!Number.isFinite(dailyFeedKg)) {
      return
    }

    currentFeedTotal += dailyFeedKg
    lotCount += 1

    const displayLabel = group.displayLabel || currentCandidate.row._farmKey || 'Unknown Facility'
    const lotLabel = currentCandidate.row.lot ?? 'Unknown Lot'

    const debugEntry: FeedDebugRow<Row> = {
      farmKey: displayLabel,
      lot: lotLabel,
      week: currentWeekKey,
      weight: currentCandidate.weight,
      stock: currentCandidate.stock,
      temp: temperature,
      sfrPercent: sfrResult.sfr,
      dailyFeedKg,
      previousWeek: previousWeekKey ?? undefined,
      matchedPreviousWeek: false,
      debug: {
        formula: `((stock ${currentCandidate.stock} × weight ${currentCandidate.weight}g) ÷ 1000) × ${sfrResult.sfr.toFixed(2)}% ÷ 100`,
        expected: dailyFeedKg
      }
    }

    debugRows.push(debugEntry)
    debugRowByCanonical.set(canonicalKey, debugEntry)

    if (!previousWeekKey) {
      return
    }

    const previousCandidate = findClosestEntryForWeek(rows, previousWeekKey, previousWeekDate, {
      requireWeight: true,
      requireStock: true
    }, args.timeZone)

    if (!previousCandidate) {
      missingPreviousLots.push(`${displayLabel} - ${lotLabel}`)
      return
    }

    const previousWeekMap = waterByWeek.get(previousWeekKey)
    if (!previousWeekMap) {
      return
    }

    const previousLotKeys = [
      ...resolveDashboardKpiRowLotTokens(previousCandidate.row),
      ...resolveCanonicalLotGroupTokens(group),
      ...canonicalLotKeys
    ]
    const previousWaterEntry = resolveWaterRowForLot({
      weekMap: previousWeekMap,
      canonicalLotKeys: previousLotKeys
    })

    if (!previousWaterEntry || previousWaterEntry.temperature === null || previousWaterEntry.temperature === undefined) {
      return
    }

    const previousTemperature = typeof previousWaterEntry.temperature === 'number'
      ? previousWaterEntry.temperature
      : Number.parseFloat(String(previousWaterEntry.temperature))

    if (!Number.isFinite(previousTemperature)) {
      return
    }

    const previousSfrResult = interpolateSFR(previousCandidate.weight, previousTemperature, {
      species: effectiveSpecies,
      matrix: effectiveMatrix
    })
    const previousBiomassKg = (previousCandidate.stock * previousCandidate.weight) / 1000
    const previousDailyFeedKg = (previousBiomassKg * previousSfrResult.sfr) / 100
    if (!Number.isFinite(previousDailyFeedKg)) {
      return
    }

    previousFeedTotal += previousDailyFeedKg

    const debugRow = debugRowByCanonical.get(canonicalKey)
    if (debugRow) {
      debugRow.matchedPreviousWeek = true
      debugRow.previousWeek = previousWeekKey ?? undefined
      debugRow.previousDailyFeedKg = previousDailyFeedKg
      const deltaKg = (debugRow.dailyFeedKg ?? 0) - previousDailyFeedKg
      debugRow.deltaKg = deltaKg
      if (previousDailyFeedKg > 0) {
        debugRow.deltaPercent = (deltaKg / previousDailyFeedKg) * 100
      } else {
        debugRow.deltaPercent = undefined
      }
      matchedLotCount += 1
    }
  })

  const trend = previousFeedTotal > 0
    ? ((currentFeedTotal - previousFeedTotal) / Math.max(previousFeedTotal, 1)) * 100
    : 0

  const summary: FeedDebugSummary = {
    currentWeekKey: currentWeekKey ?? undefined,
    previousWeekKey: previousWeekKey ?? undefined,
    currentTotal: currentFeedTotal,
    previousTotal: previousFeedTotal,
    matchedLots: matchedLotCount,
    totalLots: lotCount
  }

  return {
    dailyFeedKg: currentFeedTotal,
    trendPercent: trend,
    debugRows,
    debugSummary: summary,
    missingPreviousLots
  }
}

export type GrowthDebugRow = {
  farmKey: string
  lot: string
  currentDate?: string
  previousDate?: string
  baselineDate?: string
  gapDays?: number
  baselineGapDays?: number
  currentWeight?: number
  previousWeight?: number
  baselineWeight?: number
  currentStock?: number
  previousStock?: number
  baselineStock?: number
  dailyGain?: number
  previousDailyGain?: number
  sgr?: number
  previousSgr?: number
  notes: string[]
  included: boolean
}

export type GrowthDebugSummary = {
  matchedLots: number
  totalLots: number
  averageDailyGain: number
  averageSGR: number | null
  historicalDailyGain: number | null
  historicalSGR: number | null
  unmatchedLots: string[]
  gapWarningLots: string[]
  longGapLots: string[]
}

export const computeGrowthRateKpi = <Row extends DatabaseRowForDashboardKpis>(args: {
  canonicalLotGroups: Array<CanonicalLotGroupForDashboardKpis & { rows: Row[] }>
  now?: Date
}): {
  growthRate: number
  growthTrend: number
  debugRows: GrowthDebugRow[]
  debugSummary: GrowthDebugSummary | null
} => {
  const now = args.now ?? new Date()
  const currentWeekKey = getWeekKey(now)
  if (!currentWeekKey) {
    return {
      growthRate: 0,
      growthTrend: 0,
      debugRows: [],
      debugSummary: null
    }
  }

  if (args.canonicalLotGroups.length === 0) {
    return {
      growthRate: 0,
      growthTrend: 0,
      debugRows: [],
      debugSummary: null
    }
  }

  const debugRows: GrowthDebugRow[] = []
  const unmatchedLots = new Set<string>()
  const gapWarningLots = new Set<string>()
  const longGapLots = new Set<string>()

  let matchedLots = 0
  let consideredLots = 0
  let totalWeightedDailyGain = 0
  let totalDailyGainStock = 0
  let totalWeightedSgr = 0
  let totalSgrStock = 0
  let totalHistoricalWeightedDailyGain = 0
  let totalHistoricalStock = 0
  let totalHistoricalWeightedSgr = 0
  let totalHistoricalSgrStock = 0

  args.canonicalLotGroups.forEach((group) => {
    const rows = group.rows
      .slice()
      .sort((a, b) => {
        const aTime = a.date ? new Date(a.date).getTime() : 0
        const bTime = b.date ? new Date(b.date).getTime() : 0
        return aTime - bTime
      })
    if (rows.length === 0) return

    const displayLabel = group.displayLabel || rows[0]?._farmKey || 'Unknown Facility'
    const lotLabel = rows[rows.length - 1]?.lot ?? rows[0]?.lot ?? 'Unknown Lot'

    const currentEntry = findLatestEntryForWeek(rows, currentWeekKey, {
      requireWeight: true,
      requireStock: true
    })

    if (!currentEntry) {
      return
    }

    consideredLots += 1

    const currentWeekOfEntry = getWeekKey(currentEntry.row.date)
    const previousEntry = findPreviousValidEntry(rows, currentEntry.index, {
      excludeWeekKey: currentWeekOfEntry,
      requirements: {
        requireWeight: true,
        requireStock: true
      }
    })

    if (!previousEntry) {
      unmatchedLots.add(`${displayLabel} - ${lotLabel}`)
      debugRows.push({
        farmKey: displayLabel,
        lot: lotLabel,
        currentDate: currentEntry.row.date,
        notes: ['No prior entry with weight and stock; skipped'],
        included: false
      })
      return
    }

    const gapDays = (currentEntry.date.getTime() - previousEntry.date.getTime()) / MS_PER_DAY
    if (!Number.isFinite(gapDays) || gapDays <= 0) {
      unmatchedLots.add(`${displayLabel} - ${lotLabel}`)
      debugRows.push({
        farmKey: displayLabel,
        lot: lotLabel,
        currentDate: currentEntry.row.date,
        previousDate: previousEntry.row.date,
        notes: ['Invalid time gap between observations; skipped'],
        included: false
      })
      return
    }

    if (gapDays > 21) {
      longGapLots.add(`${displayLabel} - ${lotLabel}`)
      unmatchedLots.add(`${displayLabel} - ${lotLabel}`)
      debugRows.push({
        farmKey: displayLabel,
        lot: lotLabel,
        currentDate: currentEntry.row.date,
        previousDate: previousEntry.row.date,
        gapDays,
        notes: [`Previous entry is ${gapDays.toFixed(1)} days old (>21 days); skipped`],
        included: false
      })
      return
    }

    const notes: string[] = []
    if (gapDays > 7) {
      gapWarningLots.add(`${displayLabel} - ${lotLabel}`)
      notes.push(`Previous entry is ${gapDays.toFixed(1)} days old (>7 days)`)
    }

    const dailyGain = (currentEntry.weight - previousEntry.weight) / gapDays
    const previousStock = previousEntry.stock

    if (!Number.isFinite(dailyGain)) {
      unmatchedLots.add(`${displayLabel} - ${lotLabel}`)
      debugRows.push({
        farmKey: displayLabel,
        lot: lotLabel,
        currentDate: currentEntry.row.date,
        previousDate: previousEntry.row.date,
        gapDays,
        notes: ['Unable to compute daily gain; skipped'],
        included: false
      })
      return
    }

    // SGR = 100 × (e^g − 1), the shared formula (findings #213).
    const sgr = computeSgrPercentFromWeights(previousEntry.weight, currentEntry.weight, gapDays) ?? null

    if (previousStock > 0) {
      totalWeightedDailyGain += dailyGain * previousStock
      totalDailyGainStock += previousStock
      if (sgr !== null && Number.isFinite(sgr)) {
        totalWeightedSgr += sgr * previousStock
        totalSgrStock += previousStock
      }
    }

    const previousWeekKey = getWeekKey(previousEntry.row.date)
    const baselineEntry = findPreviousValidEntry(rows, previousEntry.index, {
      excludeWeekKey: previousWeekKey,
      requirements: {
        requireWeight: true,
        requireStock: true
      }
    })

    let baselineDailyGain: number | null = null
    let baselineSgr: number | null = null
    let baselineGapDays: number | null = null

    if (baselineEntry) {
      baselineGapDays = (previousEntry.date.getTime() - baselineEntry.date.getTime()) / MS_PER_DAY
      if (Number.isFinite(baselineGapDays) && baselineGapDays > 0) {
        if (baselineGapDays > 21) {
          notes.push(`Historical entry gap is ${baselineGapDays.toFixed(1)} days (>21); trend unavailable`)
        } else {
          if (baselineGapDays > 7) {
            notes.push(`Historical entry gap is ${baselineGapDays.toFixed(1)} days (>7 days)`)
          }
          baselineDailyGain = (previousEntry.weight - baselineEntry.weight) / baselineGapDays
          baselineSgr = computeSgrPercentFromWeights(baselineEntry.weight, previousEntry.weight, baselineGapDays) ?? baselineSgr

          if (baselineEntry.stock > 0) {
            if (baselineDailyGain !== null && Number.isFinite(baselineDailyGain)) {
              totalHistoricalWeightedDailyGain += baselineDailyGain * baselineEntry.stock
              totalHistoricalStock += baselineEntry.stock
            }
            if (baselineSgr !== null && Number.isFinite(baselineSgr)) {
              totalHistoricalWeightedSgr += baselineSgr * baselineEntry.stock
              totalHistoricalSgrStock += baselineEntry.stock
            }
          }
        }
      } else {
        notes.push('Baseline entry has invalid date gap; trend unavailable')
      }
    } else {
      notes.push('No historical entry before prior observation; trend uses matched lots only')
    }

    matchedLots += 1

    debugRows.push({
      farmKey: displayLabel,
      lot: lotLabel,
      currentDate: currentEntry.row.date,
      previousDate: previousEntry.row.date,
      baselineDate: baselineEntry?.row.date,
      gapDays,
      baselineGapDays: baselineGapDays ?? undefined,
      currentWeight: currentEntry.weight,
      previousWeight: previousEntry.weight,
      baselineWeight: baselineEntry?.weight,
      currentStock: currentEntry.stock,
      previousStock,
      baselineStock: baselineEntry?.stock,
      dailyGain,
      previousDailyGain: baselineDailyGain ?? undefined,
      sgr: (sgr !== null && Number.isFinite(sgr)) ? sgr : undefined,
      previousSgr: (baselineSgr !== null && Number.isFinite(baselineSgr)) ? baselineSgr : undefined,
      notes: Array.from(new Set(notes)),
      included: true
    })
  })

  const totalLots = consideredLots
  const averageDailyGain = totalDailyGainStock > 0
    ? totalWeightedDailyGain / totalDailyGainStock
    : 0
  const averageSGR = totalSgrStock > 0
    ? totalWeightedSgr / totalSgrStock
    : null
  const historicalDailyGain = totalHistoricalStock > 0
    ? totalHistoricalWeightedDailyGain / totalHistoricalStock
    : null
  const historicalSGR = totalHistoricalSgrStock > 0
    ? totalHistoricalWeightedSgr / totalHistoricalSgrStock
    : null

  const growthTrend = historicalDailyGain !== null && Number.isFinite(historicalDailyGain)
    ? averageDailyGain - historicalDailyGain
    : 0

  const summary: GrowthDebugSummary = {
    matchedLots,
    totalLots,
    averageDailyGain,
    averageSGR,
    historicalDailyGain,
    historicalSGR,
    unmatchedLots: Array.from(unmatchedLots),
    gapWarningLots: Array.from(gapWarningLots),
    longGapLots: Array.from(longGapLots)
  }

  return {
    growthRate: averageDailyGain,
    growthTrend,
    debugRows,
    debugSummary: summary
  }
}

export type MortalityDebugRow = {
  farmKey: string
  lot: string
  currentDate?: string
  previousDate?: string
  baselineDate?: string
  currentStock?: number
  previousStock?: number
  baselineStock?: number
  mortalityCount?: number
  mortalityRate?: number
  previousMortalityCount?: number
  previousMortalityRate?: number
  baselineGapDays?: number
  gapDays?: number
  gapFlag?: boolean
  notes: string[]
  included: boolean
  _matchedFor?: string
}

export type MortalityDebugSummary = {
  matchedLots: number
  totalLots: number
  currentMortalityCount: number
  currentPreviousStock: number
  currentRate: number
  previousMortalityCount: number
  previousPreviousStock: number
  previousRate: number | null
  unmatchedLots: string[]
  gapWarningLots: string[]
  longGapLots: string[]
}

const toDateTime = (value: string | null | undefined): number => {
  if (!value) return Number.NEGATIVE_INFINITY
  const date = new Date(value)
  const time = date.getTime()
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time
}

export const computeMortalityKpi = (args: {
  canonicalLotGroups: CanonicalLotGroupForDashboardKpis[]
  now?: Date
}): {
  mortalityRate: number
  mortalityTrend: number
  debugRows: MortalityDebugRow[]
  debugSummary: MortalityDebugSummary | null
} => {
  const now = args.now ?? new Date()
  const currentWeekKey = getWeekKey(now)
  if (!currentWeekKey) {
    return {
      mortalityRate: 0,
      mortalityTrend: 0,
      debugRows: [],
      debugSummary: null
    }
  }

  const rowsByLot = new Map<string, DatabaseRowForDashboardKpis[]>()
  args.canonicalLotGroups.forEach((group) => {
    const key = `${group.canonicalProductionUnitKey}||${group.canonicalLotKey}`
    rowsByLot.set(key, group.rows.slice().sort((a, b) => toDateTime(a.date) - toDateTime(b.date)))
  })

  if (rowsByLot.size === 0) {
    return {
      mortalityRate: 0,
      mortalityTrend: 0,
      debugRows: [],
      debugSummary: null
    }
  }

  const debugRows: MortalityDebugRow[] = []
  const unmatchedLots = new Set<string>()
  const gapWarningLots = new Set<string>()
  const longGapLots = new Set<string>()

  let totalMortalityCount = 0
  let totalPreviousStock = 0
  let totalHistoricalMortalityCount = 0
  let totalHistoricalPreviousStock = 0
  let matchedLots = 0
  let consideredLots = 0

  rowsByLot.forEach((rows: DatabaseRowForDashboardKpis[], lotKey) => {
    const separatorIndex = lotKey.indexOf('||')
    const productionUnitKey = separatorIndex >= 0 ? lotKey.slice(0, separatorIndex) : lotKey
    const lotCanonicalKey = separatorIndex >= 0 ? lotKey.slice(separatorIndex + 2) : lotKey

    const group = args.canonicalLotGroups.find((entry) =>
      entry.canonicalProductionUnitKey === productionUnitKey && entry.canonicalLotKey === lotCanonicalKey
    )
    const displayLabel = group?.displayLabel ?? rows[0]?._farmKey ?? 'Unknown Facility'
    const lot = rows[0]?.lot ?? 'Unknown Lot'

    const currentSelection = rows.reduce<{
      row: DatabaseRowForDashboardKpis
      index: number
      date: Date
    } | null>((best, row, index) => {
      if (!row.date) return best
      const rowWeekKey = getWeekKey(row.date)
      if (rowWeekKey !== currentWeekKey) return best
      const stock = parseFishStock(row.fishStock)
      if (stock <= 0) return best
      const rowDate = new Date(row.date)
      if (Number.isNaN(rowDate.getTime())) return best

      if (!best || rowDate.getTime() >= best.date.getTime()) {
        return { row, index, date: rowDate }
      }

      return best
    }, null)

    if (!currentSelection) return

    const currentRowIndex = currentSelection.index
    const resolvedCurrentRow = currentSelection.row
    const resolvedCurrentDate = currentSelection.date

    consideredLots += 1

    const currentStock = parseFishStock(resolvedCurrentRow.fishStock)
    if (currentStock <= 0) {
      debugRows.push({
        farmKey: displayLabel,
        lot,
        currentDate: resolvedCurrentRow.date,
        currentStock,
        notes: ['Current week entry missing fish stock; skipped'],
        included: false
      })
      unmatchedLots.add(`${displayLabel} - ${lot}`)
      return
    }

    let previousRow: DatabaseRowForDashboardKpis | null = null
    let previousRowIndex = -1
    let previousDate: Date | null = null

    const currentRowWeekKey = getWeekKey(resolvedCurrentRow.date)

    for (let idx = currentRowIndex - 1; idx >= 0; idx--) {
      const candidate = rows[idx]
      if (!candidate.date) continue
      const candidateStock = parseFishStock(candidate.fishStock)
      if (candidateStock <= 0) continue
      const candidateDate = new Date(candidate.date)
      if (Number.isNaN(candidateDate.getTime())) continue

      const candidateWeekKey = getWeekKey(candidate.date)
      if (candidateWeekKey === currentRowWeekKey) {
        continue
      }

      previousRow = candidate
      previousRowIndex = idx
      previousDate = candidateDate
      break
    }

    if (!previousRow || !previousDate) {
      debugRows.push({
        farmKey: displayLabel,
        lot,
        currentDate: resolvedCurrentRow.date,
        currentStock,
        notes: ['No prior entry found; treated as first observation'],
        included: false
      })
      unmatchedLots.add(`${displayLabel} - ${lot}`)
      return
    }

    const gapDays = (resolvedCurrentDate.getTime() - previousDate.getTime()) / MS_PER_DAY
    if (gapDays > 21) {
      debugRows.push({
        farmKey: displayLabel,
        lot,
        currentDate: resolvedCurrentRow.date,
        previousDate: previousRow.date,
        previousStock: parseFishStock(previousRow.fishStock),
        currentStock,
        gapDays,
        gapFlag: true,
        notes: [`Previous entry is ${gapDays.toFixed(1)} days old (>21 days); skipping lot`],
        included: false
      })
      longGapLots.add(`${displayLabel} - ${lot}`)
      unmatchedLots.add(`${displayLabel} - ${lot}`)
      return
    }

    if (gapDays > 7) {
      gapWarningLots.add(`${displayLabel} - ${lot}`)
    }

    const previousStock = parseFishStock(previousRow.fishStock)
    if (previousStock <= 0) {
      debugRows.push({
        farmKey: displayLabel,
        lot,
        currentDate: resolvedCurrentRow.date,
        previousDate: previousRow.date,
        currentStock,
        notes: ['Previous entry missing fish stock; skipped'],
        included: false
      })
      unmatchedLots.add(`${displayLabel} - ${lot}`)
      return
    }

    const mortalityCount = Math.max(previousStock - currentStock, 0)
    const mortalityRate = previousStock > 0 ? (mortalityCount / previousStock) * 100 : 0

    let previousMortalityCount: number | null = null
    let previousMortalityRate: number | null = null
    let baselineNote: string | null = null
    let baselineGapDays: number | null = null
    let baselineDateValue: Date | null = null
    let baselineStockValue: number | null = null

    if (previousRowIndex > 0) {
      let foundBaseline = false
      for (let idx = previousRowIndex - 1; idx >= 0; idx--) {
        const candidate = rows[idx]
        if (!candidate.date) continue
        const candidateStock = parseFishStock(candidate.fishStock)
        if (candidateStock <= 0) continue
        const candidateDate = new Date(candidate.date)
        if (Number.isNaN(candidateDate.getTime())) continue

        const candidateWeekKey = getWeekKey(candidate.date)
        if (candidateWeekKey === getWeekKey(previousRow.date)) {
          continue
        }

        const priorGapDays = (previousDate.getTime() - candidateDate.getTime()) / MS_PER_DAY
        if (priorGapDays > 21) {
          baselineNote = `Historical entry gap to prior data is ${priorGapDays.toFixed(1)} days (>21); trend unavailable`
          baselineGapDays = priorGapDays
          break
        }

        foundBaseline = true
        baselineGapDays = priorGapDays
        baselineDateValue = candidateDate
        baselineStockValue = candidateStock
        if (candidateStock > 0) {
          previousMortalityCount = Math.max(candidateStock - previousStock, 0)
          previousMortalityRate = (previousMortalityCount / candidateStock) * 100
          totalHistoricalMortalityCount += previousMortalityCount
          totalHistoricalPreviousStock += candidateStock
        } else {
          baselineNote = 'Baseline entry has invalid fish stock; trend unavailable'
        }
        break
      }

      if (!foundBaseline && !baselineNote) {
        baselineNote = 'No earlier entries with fish stock prior to historical row'
      }
    } else {
      baselineNote = 'Historical entry is the first recorded observation'
    }

    matchedLots += 1
    totalMortalityCount += mortalityCount
    totalPreviousStock += previousStock

    const notes: string[] = []
    if (gapDays > 7) {
      notes.push(`Previous entry is ${gapDays.toFixed(1)} days old (>7 days)`)
    }
    if (baselineGapDays !== null && baselineGapDays > 7) {
      notes.push(`Baseline entry is ${baselineGapDays.toFixed(1)} days old (>7 days)`)
    }
    if (baselineNote) {
      notes.push(baselineNote)
    }
    if (previousMortalityCount === null && !baselineNote) {
      notes.push('No baseline before historical entry; trend uses available lots only')
    }

    debugRows.push({
      farmKey: displayLabel,
      lot,
      currentDate: resolvedCurrentRow.date,
      previousDate: previousRow.date,
      previousStock,
      currentStock,
      mortalityCount,
      mortalityRate,
      previousMortalityCount: previousMortalityCount ?? undefined,
      previousMortalityRate: previousMortalityRate ?? undefined,
      baselineDate: baselineDateValue ? baselineDateValue.toISOString() : undefined,
      baselineStock: baselineStockValue ?? undefined,
      baselineGapDays: baselineGapDays ?? undefined,
      gapDays,
      gapFlag: gapDays > 7,
      notes: Array.from(new Set(notes)),
      included: true
    })
  })

  const totalLots = consideredLots
  const currentRate = totalPreviousStock > 0 ? (totalMortalityCount / totalPreviousStock) * 100 : 0
  const previousRate = totalHistoricalPreviousStock > 0
    ? (totalHistoricalMortalityCount / totalHistoricalPreviousStock) * 100
    : null
  const mortalityTrend = previousRate !== null ? currentRate - previousRate : 0

  const summary: MortalityDebugSummary = {
    matchedLots,
    totalLots,
    currentMortalityCount: totalMortalityCount,
    currentPreviousStock: totalPreviousStock,
    currentRate,
    previousMortalityCount: totalHistoricalMortalityCount,
    previousPreviousStock: totalHistoricalPreviousStock,
    previousRate,
    unmatchedLots: Array.from(unmatchedLots),
    gapWarningLots: Array.from(gapWarningLots),
    longGapLots: Array.from(longGapLots)
  }

  return {
    mortalityRate: currentRate,
    mortalityTrend,
    debugRows,
    debugSummary: summary
  }
}
